import { Client, CommunityRepository, UserRepository } from "@amityco/ts-sdk";
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useQuery, useQueryClient } from "react-query";

import { getAmityAuth, getCommunityGroups } from "@/api";
import { APP_ID } from "@/env.ts";
import { useAppProperties, useMainTabs, useUserDetails } from "@/hooks";
import { useTranslate, useUser } from "@/providers/context";
import { toPascalCase } from "@/utils";
import { getCommunities, isAutoJoinGroup } from "@/app/modules/Community/util";

type AuthState = {
  state: "success" | "loading" | "failed";
  authenticatedUser?: Amity.User;
};

interface ContextValue {
  authState: AuthState;
  autoGroupsJoined?: boolean;
  communityTabName: string;
  isGlobalBanned: boolean;
  isError: boolean;
  isLoading: boolean;
  userIsSubscribed: boolean;
  displayName: string;
}

interface ProviderProps {
  children: ReactNode | ReactNode[];
}

type OptionalContextValue = ContextValue | undefined;
const AmityAuthContext = createContext<OptionalContextValue>(undefined);

const AmityAuthProvider = ({ children }: ProviderProps) => {
  const { communityTab, data: mainTabs } = useMainTabs();
  const { data: properties, rolloutCommunity } = useAppProperties();
  const { uid, userIsSubscribed } = useUser();
  const { data: userDetails } = useUserDetails();
  const queryClient = useQueryClient();
  const { translate } = useTranslate();

  const [autoJoinGroupIds, setAutoJoinGroupIds] = useState<string[]>(); // Groups with type "main" or "automatic" need to be automatically joined
  const [autoGroupsJoined, setAutoGroupsJoined] = useState<boolean>(false);

  useEffect(() => {
    const getAutoGroups = async () => {
      const groups = await getCommunityGroups();

      if (groups) {
        const autoGroups = groups?.filter((group) => isAutoJoinGroup(group));
        setAutoJoinGroupIds(autoGroups.map((group) => group.CommunityID));
      } else if (communityTab?.Properties?.CommunityID) {
        setAutoJoinGroupIds([communityTab.Properties.CommunityID]);
      } else {
        setAutoJoinGroupIds([]);
      }
    };

    if (!autoJoinGroupIds && !autoGroupsJoined) {
      getAutoGroups();
    }
  }, [autoJoinGroupIds, communityTab, autoGroupsJoined]);

  const communityTabName = toPascalCase(
    communityTab?.Name ?? translate("Community"),
  );

  const communityEnabled =
    !!rolloutCommunity && !!communityTab && !!uid && !!userIsSubscribed;

  const [sessionState, setSessionState] = useState<Amity.SessionStates>();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isGlobalBanned, setIsGlobalBanned] = useState(false);
  const [error, setError] = useState<
    { msg: string; rawError?: unknown } | undefined
  >();
  const [authenticatedUser, setAuthenticatedUser] = useState<Amity.User>();

  const query = useQuery(["amity_auth", uid], () => getAmityAuth(), {
    enabled: communityEnabled,
  });

  const amityAuthToken = query.data;
  const amityClientKey = properties?.AmityClientKey;
  const amityRegion = properties?.AmityRegion;

  const displayName = userDetails
    ? `${userDetails?.first_name ?? ""} ${userDetails?.last_name ?? ""}`
    : "anonymous";

  const login = useCallback(async () => {
    if (isLoggedIn || !uid || !userDetails || !APP_ID) {
      return;
    }

    try {
      if (!amityAuthToken === undefined) {
        return setError({ msg: "failed to get authToken" });
      }

      const isLoginSuccess = await Client.login(
        { userId: uid, authToken: amityAuthToken },
        {
          sessionWillRenewAccessToken: ({
            renewWithAuthToken,
            unableToRetrieveAuthToken,
          }) =>
            amityAuthToken
              ? renewWithAuthToken(amityAuthToken)
              : unableToRetrieveAuthToken(),
        },
      );

      setIsLoggedIn(isLoginSuccess);
      if (!isLoginSuccess) {
        setError({ msg: "amity login failure" });
        return;
      }

      setIsLoggedIn(true);
      UserRepository.updateUser(uid, { displayName });
    } catch (e) {
      if (
        e instanceof Error &&
        e.toString().includes("User is global banned")
      ) {
        setIsGlobalBanned(true);
      }
      return setError({ msg: "amity login failure", rawError: e });
    }
  }, [
    uid,
    userDetails,
    displayName,
    isLoggedIn,
    amityAuthToken,
    setIsLoggedIn,
    setError,
    setIsGlobalBanned,
  ]);

  useEffect(() => {
    if (!isLoggedIn && uid && userDetails && amityAuthToken) {
      login();
    }
  }, [isLoggedIn, uid, userDetails, amityAuthToken, login]);

  useEffect(() => {
    if (isLoggedIn && uid) {
      const unsubscribe = UserRepository.getUser(uid, ({ data }) => {
        if (data) {
          const user = {
            ...data,
            displayName: data.displayName ?? displayName,
          };
          setAuthenticatedUser(user);
        }
      });

      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    }
  }, [isLoggedIn, uid, displayName]);

  useEffect(() => {
    if (!amityClientKey || !amityRegion) {
      return;
    }

    try {
      Client.createClient(amityClientKey, amityRegion);
      Client.onSessionStateChange(setSessionState);
    } catch (e) {
      setError({ msg: "failed to connect to client" });
    }
  }, [amityClientKey, amityRegion]);

  const authState: AuthState = useMemo(() => {
    if (!mainTabs || !autoJoinGroupIds) {
      return { state: "loading" };
    }

    if (
      !communityEnabled ||
      error ||
      sessionState === "terminated" ||
      sessionState === "notLoggedIn"
    ) {
      return { state: "failed" };
    }

    if (
      isLoggedIn &&
      !error &&
      authenticatedUser &&
      (sessionState === "established" || sessionState === "tokenExpired")
    ) {
      return { state: "success", authenticatedUser };
    }

    return { state: "loading" };
  }, [
    autoJoinGroupIds,
    mainTabs,
    communityEnabled,
    sessionState,
    error,
    authenticatedUser,
    isLoggedIn,
  ]);

  useEffect(() => {
    if (
      authState.state === "success" &&
      authState.authenticatedUser &&
      autoJoinGroupIds &&
      !autoGroupsJoined
    ) {
      // Make sure this user has joined all of the automatic groups
      const joinAutoGroups = async () => {
        const joinedCommunities = await getCommunities("member");
        const joinPromises: Promise<void>[] = [];

        for (const groupId of autoJoinGroupIds) {
          const joinedGroup = joinedCommunities.find(
            ({ communityId }) => communityId === groupId,
          );

          if (!joinedGroup) {
            const joinPromise = CommunityRepository.joinCommunity(groupId)
              .then(() => {})
              .catch((e) => {
                const errorMessage = e.toString();
                if (
                  errorMessage.includes("User is banned from this community")
                ) {
                  setIsGlobalBanned(true);
                  throw new Error(errorMessage);
                }
                throw e;
              });

            joinPromises.push(joinPromise);
          }
        }

        try {
          await Promise.all(joinPromises);
          await queryClient.invalidateQueries(["community_groups", uid]);
          setAutoGroupsJoined(true);
        } catch (error) {
          console.error("Failed to join some communities:", error);
        }
      };

      joinAutoGroups();
    }
  }, [autoJoinGroupIds, autoGroupsJoined, authState, queryClient, uid]);

  return (
    <AmityAuthContext.Provider
      value={{
        authState,
        communityTabName,
        isGlobalBanned,
        isError: authState.state === "failed",
        isLoading: authState.state === "loading" || !autoGroupsJoined,
        userIsSubscribed,
        displayName,
      }}
    >
      {children}
    </AmityAuthContext.Provider>
  );
};

export { AmityAuthContext, AmityAuthProvider };
